import {
  BankOutlined,
  LockOutlined,
  MailOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  RadioChangeEvent
} from 'antd';
import { useAxiosRequest, useIsDark } from 'common/hooks';
import { useEffect, useState } from 'react';
import ReactFlagsSelect from "react-flags-select";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { shallowEqual, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BillingModal from './BillingModal';
import logo from './logo.png';
import SelectedPlanBanner from './SelectedPlanBanner';
import "./signup.scss";

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const SignupForm = () => {
  const isDark = useIsDark();
  const [value, setValue] = useState('organization');
  const [open, setOpen] = useState(false)
  const [billingId, setbillingId] = useState()
  const [accountId, setAccountId] = useState<number | undefined>()
  const [selected, setSelected] = useState<string>("");
  const [termsOpen, setTermsOpen] = useState(false)
  const { auth } = useSelector((state: any) => state, shallowEqual);
  const email = useSelector(
    (state: any) => state.auth?.profileData?.otpVerifiedEmail
  );
  const signupContext = useSelector(
    (state: any) => state.auth?.profileData?.signupContext
  ) || {};
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const postSignUp = useAxiosRequest('');
  const { packageId } = useParams()

  const isTrial: boolean = !!signupContext.isTrial;
  const billingPeriod: 'Monthly' | 'Yearly' = signupContext.billingPeriod || 'Monthly';
  const resolvedPackageId: number = Number(signupContext.planId ?? packageId ?? 0);
  const trialSlotsUnlimited =
    isTrial && signupContext.trialEmployeeSlots === -1;
  const trialSlotsCap =
    isTrial && typeof signupContext.trialEmployeeSlots === 'number' && signupContext.trialEmployeeSlots > 0
      ? signupContext.trialEmployeeSlots
      : undefined;

  const employeeSlotsWatch = Form.useWatch('employeeSlots', form) ?? 0;
  const slotsNum = Number(employeeSlotsWatch) || 0;
  const monthsFactor = billingPeriod === 'Yearly' ? 12 : 1;
  const ammount = isTrial
    ? 0
    : (Number(signupContext.effectivePricePerUser) || 0) * slotsNum * monthsFactor;

  useEffect(() => {
    if (auth.isAuth) {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const onChange = (e: RadioChangeEvent) => {
    setValue(e.target.value);
  };

  const onFinish = async (values: any) => {
    const { fullname, createPassword, company, employeeSlots, phoneNumber } = values;
    const payload = {
      strLoginId: email,
      strPasswordHash: createPassword,
      strName: fullname,
      phoneNumber: phoneNumber ? `+${phoneNumber}` : '',
      isActive: true,
      userTypeId: 1,
      strCompanyName: company,
      country: selected ? (regionNames.of(selected) || selected) : '',
      intPackageId: resolvedPackageId,
      strBillingPeriod: billingPeriod,
      employeeSlots: Number(employeeSlots) || 0,
      withTrial: isTrial,
      isEmailVerified: true,
    };
    if (email) {
      postSignUp.apiAction({
        urlObjKey: 'SignUp',
        method: 'POST',
        payload: payload,
        isToast: true,
        cb: (data) => {
          // `trialGranted` is set by the API when the platform toggle
          // force_trial_on_paid_signup is ON and this PAID-plan signup was given a free
          // trial on the plan it selected. There is no invoice in that case, so
          // `billingId` is null — opening the billing modal would show a payment sheet
          // with no bill to pay. Must be checked alongside our own `isTrial`.
          const grantedTrial = isTrial || data?.trialGranted === true;

          if (grantedTrial) {
            // Trial accounts don't go through billing
            navigate(
              `/${resolvedPackageId}/${signupContext.planType || 'Trial'}/signup/success`,
              { state: { email, password: createPassword } }
            );
            return;
          }
          setOpen(true);
          console.log('Billing info:', data);
          setbillingId(data?.billingId);
          setAccountId(data?.accountId);
        },
        onError: () => {
        },
      });
    }
  };
  const onFinishFailed = () => { };
  return (
    <div className='flex flex-col justify-center items-center gap-4 w-full py-10'>
      <div className='flex flex-col justify-center items-center gap-4 z-50'>
        <Link to="/" ><img src={logo} alt="Trackforce Logo" className='w-[120px] max-w-[200px] object-contain ' /></Link>
        <h1 className={`text-2xl font-semibold ${isDark ? 'text-white' : 'text-[#334155]'}`}>Welcome to Trackforce</h1>
      </div>
      <SelectedPlanBanner isDark={isDark} />
      <div className='w-full'>
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          className="w-full"
        >
          <div className="flex justify-center items-center mb-5">
            <Radio.Group
              onChange={onChange}
              value={value}
              className="flex gap-5"
            >
              <Radio value={'organization'}>Organization</Radio>
              <Radio value={'employee'} disabled>
                Employee
              </Radio>
            </Radio.Group>
          </div>
          <Form.Item
            label={
              <span className={`text-md font-bold ${isDark ? 'text-white' : 'text-[#333]'}`}>
                Full Name
                <span style={{ color: 'red', }}>*</span>
              </span>
            }
            name="fullname"
            className="mb-6 md:mb-3 w-full"
            rules={[
              { required: true, message: 'Please input your full name', validateTrigger: 'onSubmit' },
              { min: 3, message: 'Name should be more than 2 characters', validateTrigger: 'onChange' },
              {
                pattern: /^[a-zA-Z\s]+$/,
                message: 'Name should not contain numeric values or special characters',
                validateTrigger: 'onChange',
              },
            ]}
            validateFirst={true}
            hasFeedback
          >

            <Input
              placeholder="Enter Your Full Name"
              size='large'
              prefix={
                <UserOutlined className="site-form-item-icon mr-1 text-gray-500" />
              }
              className='w-full '
            />
          </Form.Item>
          <Form.Item
            label={
              <span className={`text-md font-bold ${isDark ? 'text-white' : 'text-[#333]'}`}>
                Email<span style={{ color: 'red', }}>*</span>
              </span>
            }
            name="email"
            className="mb-6 md:mb-3 w-full"
            rules={[
              { type: 'email', message: 'Please, enter a valid email' },
            ]}
            hasFeedback
          >
            <Input
              placeholder="Enter Your Email"
              value={email}
              defaultValue={email}
              size='large'
              prefix={
                <MailOutlined className="site-form-item-icon mr-1 text-gray-500" />
              }
              readOnly
            />
          </Form.Item>
          <Form.Item
            label={
              <span className={`text-md font-bold ${isDark ? 'text-white' : 'text-[#333]'}`}>
                Phone Number
                <span style={{ color: 'red', marginLeft: 3 }}>*</span>
              </span>
            }
            name="phoneNumber"
            className="mb-6 md:mb-3"
            rules={[
              { required: true, message: 'Please enter your phone number', validateTrigger: 'onSubmit' },
              {
                pattern: /^[0-9]{8,15}$/,
                message: 'Please enter a valid phone number',
                validateTrigger: 'onBlur',
              },
            ]}
            hasFeedback
          >
            <PhoneInput
              country={'us'}
              enableSearch
              placeholder="Enter your phone number"
              containerClass={`tf-phone-input${isDark ? ' tf-phone-dark' : ''}`}
            />
          </Form.Item>
          <Form.Item
            label={
              <span className={`text-md font-bold ${isDark ? 'text-white' : 'text-[#333]'}`}>
                Password
                <span style={{ color: 'red', marginLeft: 3 }}>*</span>
              </span>
            }
            name="createPassword"
            className="mb-6 md:mb-2"
            rules={[
              { required: true, message: 'Please input a password', validateTrigger: 'onSubmit' },
              { min: 6, message: 'Password must be at least 6 characters', validateTrigger: 'onChange' },
              {
                pattern: /^(?=.*\d)/,
                message: 'Password must contain at least one number',
                validateTrigger: 'onChange',
              },
              {
                pattern: /^(?=.*[A-Z])/,
                message: 'Password must contain at least one uppercase letter',
                validateTrigger: 'onChange',
              },
              {
                pattern: /^(?=.*[a-z])/,
                message: 'Password must contain at least one lowercase letter',
                validateTrigger: 'onChange',
              },
              {
                pattern: /^(?=.*[@$#!%?&])/,
                message: 'Password must contain at least one special character',
                validateTrigger: 'onChange',
              },
            ]}
            validateFirst={true} // Ensures only one error message is displayed at a time
            hasFeedback
          >
            <Input.Password
              size='large'
              placeholder="Create a password"
              prefix={
                <LockOutlined className="site-form-item-icon mr-1 text-gray-500" />
              }
            />
          </Form.Item>
          <Form.Item
            label={
              <span className={`text-md font-bold ${isDark ? 'text-white' : 'text-[#333]'}`}>
                Repeat Password
                <span style={{ color: 'red', marginLeft: 3 }}>*</span>
              </span>
            }
            name="confirmPassword"
            className="mb-6 md:mb-2"
            dependencies={['createPassword']}
            rules={[
              { required: true, message: 'Please confirm password' },
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  if (!value || getFieldValue('createPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject('Password does not match');
                },
              }),
            ]}
            hasFeedback
          >
            <Input.Password
              size='large'
              placeholder="Confirm Password"
              prefix={
                <LockOutlined className="site-form-item-icon mr-1 text-gray-500" />
              }
            />
          </Form.Item>
          <Form.Item
            label={
              <span className={`text-md font-bold ${isDark ? 'text-white' : 'text-[#333]'}`}>
                Organization Name
                <span style={{ color: 'red', marginLeft: 3 }}>*</span>
              </span>
            }
            name="company"
            className="mb-6 md:mb-3"
            rules={[
              { required: true, message: 'Please enter your company name' },
            ]}
            hasFeedback
          >
            <Input
              size='large'
              placeholder="Enter Your Company Name"
              prefix={
                <BankOutlined className="site-form-item-icon mr-1 text-gray-500" />
              }
            />
          </Form.Item>
          <Form.Item
            label={
              <span className={`text-md font-bold ${isDark ? 'text-white' : 'text-[#333]'}`}>
                Country
                <span style={{ color: 'red', marginLeft: 3 }}>*</span>
              </span>
            }
            name="country"
            className="mb-6 md:mb-3"
            rules={[{ required: true, message: 'Please select your country' }]}
          >
            <ReactFlagsSelect
              selected={selected}
              onSelect={(code) => {
                setSelected(code);
                form.setFieldValue('country', code);
              }}
              searchable
              searchPlaceholder="Search country..."
              placeholder="Select your country"
              className={`tf-country-select${isDark ? ' tf-country-dark' : ''}`}
              selectButtonClassName="tf-country-btn"
            />
          </Form.Item>
          {
            !isTrial && <Form.Item
              label={
                <span className={`text-md font-bold ${isDark ? 'text-white' : 'text-[#333]'}`}>
                  Employee Slots
                  <span style={{ color: 'red', marginLeft: 3 }}>*</span>
                  {isTrial && (
                    <span className='ml-2 text-xs font-normal text-blue-600'>
                      (trial allows up to{' '}
                      {trialSlotsUnlimited
                        ? 'Unlimited'
                        : trialSlotsCap ?? signupContext.trialEmployeeSlotsLabel}
                      )
                    </span>
                  )}
                </span>
              }
              name="employeeSlots"
              className="mb-6 md:mb-6"
              rules={[
                { required: true, message: 'Please enter the number of employees' },
                {
                  validator: (_, value) => {
                    if (value == null || value === '') return Promise.resolve();
                    const n = Number(value);
                    if (!Number.isFinite(n) || n < 1) {
                      return Promise.reject('Must be at least 1');
                    }
                    if (!Number.isInteger(n)) {
                      return Promise.reject('Must be a whole number');
                    }
                    if (trialSlotsCap !== undefined && n > trialSlotsCap) {
                      return Promise.reject(
                        `Trial allows up to ${trialSlotsCap} employees`
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              hasFeedback
            >
              <InputNumber
                size='large'
                min={1}
                max={trialSlotsCap}
                placeholder='How many employees do you want to track?'
                className='w-full'
                prefix={
                  <TeamOutlined className='site-form-item-icon mr-1 text-gray-500' />
                }
              />
            </Form.Item>
          }
          {!isTrial && slotsNum > 0 && signupContext.effectivePricePerUser ? (
            <div className={`mb-4 text-sm rounded-lg px-3 py-2 flex justify-between items-center ${isDark ? 'text-[#CBD5E1] bg-[#0F1E33]' : 'text-gray-600 bg-gray-50'}`}>
              <span>
                {signupContext.planName || 'Plan'} · {billingPeriod}
              </span>
              <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                {signupContext.currencySymbol || '$'}
                {ammount.toFixed(2)}
                <span className={`text-xs font-normal ml-1 ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
                  / {billingPeriod === 'Yearly' ? 'yr' : 'mo'}
                </span>
              </span>
            </div>
          ) : null}
          <Form.Item
            name="agreement"
            valuePropName="checked"
            className='-mt-2 '
            rules={[
              {
                validator: (_, value) => {
                  if (value) return Promise.resolve();
                  return Promise.reject('Please accepted the agreement');
                },
              },
            ]}
          >
            <Checkbox>
              I agree with{' '}
              <span
                onClick={(e) => { e.preventDefault(); setTermsOpen(true); }}
                className="text-primaryColor cursor-pointer hover:underline"
              >
                terms & conditions
              </span>
            </Checkbox>
          </Form.Item>
          <Form.Item className="">
            <Button
              type="primary"
              htmlType="submit"
              size='large'
              loading={postSignUp.isLoading}
              iconPosition='end'
              className="bg-primaryColor w-full font-bold text-[#FFFFFF] rounded-[8px]">
              {postSignUp.isLoading ? "Processing" : 'Sign Up'}
            </Button>
          </Form.Item>
        </Form>
        {/* <Divider >Or</Divider> */}
        <div>
          {/* <div className='hidden grid-cols-2 justify-center items-center gap-2'>
            <Button
              block
              disabled
              className={`border rounded-xl h-11 text-md font-semibold flex items-center justify-center ${isDark ? 'border-[#2E3138] !text-white' : 'border-[#DCE3E3] text-[#000000]'}`}
            >
              <GoogleIcon />
              <span className="tracking-[-0.16px]">Sign up with Google</span>
            </Button>
            <Button
              block
              disabled
              className={`border rounded-xl h-11 text-md font-semibold ${isDark ? 'border-[#2E3138] !text-white' : 'border-[#DCE3E3] text-[#000000]'}`}
            >
              <SsoIcon />
              <span className=" tracking-[-0.16px"> Sign up with SSO</span>
            </Button>
          </div> */}
          <p className={`text-center w-full mt-4 z-[999]! ${isDark ? 'text-[#CBD5E1]' : 'text-[#334155]'}`}>
            Already have an account?{' '}
            <Link
              to={`/login`}
              className="text-sm text-primaryColor font-semibold"
            >
              Login
            </Link>
          </p>
        </div>
      </div>
      <BillingModal
        billingId={billingId}
        ammount={ammount}
        accountId={accountId}
        open={open}
        setOpen={setOpen}
      />

      <Modal
        open={termsOpen}
        onCancel={() => setTermsOpen(false)}
        footer={<Button type="primary" onClick={() => setTermsOpen(false)}>I Understand</Button>}
        title="Terms & Conditions"
        centered
        width={600}
      >
        <div className="max-h-[60vh] overflow-y-auto custom-scrollbar pr-2 text-sm text-gray-700 space-y-4">
          <p className="font-semibold text-gray-900">Last updated: May 2025</p>

          <section>
            <h3 className="font-semibold text-gray-900 mb-1">1. Acceptance of Terms</h3>
            <p>By creating an account and using Trackforce, you agree to be bound by these Terms & Conditions. If you do not agree, please do not use the service.</p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-1">2. Account Responsibilities</h3>
            <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized use.</p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-1">3. Subscription & Billing</h3>
            <p>Subscriptions are billed on a monthly or yearly basis as selected during sign-up. All fees are non-refundable except as required by law. We reserve the right to modify pricing with 30 days&apos; notice.</p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-1">4. Data Privacy</h3>
            <p>We collect and process personal data in accordance with our Privacy Policy. Employee data uploaded to the platform is owned by your organization. We do not sell your data to third parties.</p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-1">5. Acceptable Use</h3>
            <p>You agree not to use Trackforce for any unlawful purpose, to infringe on any third-party rights, or to interfere with the platform&apos;s normal operation. Violations may result in account suspension.</p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-1">6. Limitation of Liability</h3>
            <p>Trackforce is provided &quot;as is&quot; without warranties of any kind. We shall not be liable for any indirect, incidental, or consequential damages arising from your use of the service.</p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-1">7. Termination</h3>
            <p>Either party may terminate the agreement at any time. Upon termination, your access will be revoked and your data retained for 30 days before deletion.</p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-1">8. Contact</h3>
            <p>For questions about these terms, contact us at <span className="text-primaryColor">support@trackforce.io</span>.</p>
          </section>
        </div>
      </Modal>
    </div>
  )
}

export default SignupForm
